import { type FileRulesType } from 'utils/apiList/files'
import { getImageDimension } from 'utils/helpers/image'
import { HOME_TAB_ICON_DIMENSION } from 'features/MultiCategory/constant/homeTabCreateEdit'

/**
 * File-service rules list extensions without a shared shape — `.svg`, `svg` and
 * `image/svg+xml` all show up — so everything is normalised to a bare `svg`.
 */
const toBareExtension = (extension: string) =>
  extension
    .replace(/^image\//, '')
    .replace(/^\./, '')
    .replace('+xml', '')
    .toLowerCase()

/**
 * The icon has to be exactly 96x96px. File-service rules only cap the size, so
 * the dimensions are read off the file itself.
 */
const validateIconDimension = async (file: File) => {
  const previewUrl = URL.createObjectURL(file)

  try {
    const { width, height } = await getImageDimension(previewUrl)

    return width === HOME_TAB_ICON_DIMENSION && height === HOME_TAB_ICON_DIMENSION
      ? ''
      : `Dimensi icon harus ${HOME_TAB_ICON_DIMENSION}x${HOME_TAB_ICON_DIMENSION}px`
  } catch {
    return 'Gagal membaca file gambar'
  } finally {
    URL.revokeObjectURL(previewUrl)
  }
}

/** Returns the rule violation message, or an empty string when the file passes. */
export const validateIconFile = async (file: File, fileRules: FileRulesType) => {
  if (file.size > fileRules.maxFileSize) {
    const maxFileSizeMb = Math.round(fileRules.maxFileSize / 1000 / 1000)

    return `Maksimum file size ${maxFileSizeMb}MB`
  }

  const extensions = fileRules.extensions.map(toBareExtension)
  const fileExtension = toBareExtension(file.name.split('.').pop() || '')

  /* An empty rule list means the service did not restrict the format. */
  if (extensions.length && !extensions.includes(fileExtension)) {
    return `Format file harus ${extensions.join(', ').toUpperCase()}`
  }

  return validateIconDimension(file)
}

/** `image/svg+xml` → `SVG` */
export const getFileRuleExtensionLabels = (fileRules: FileRulesType) =>
  fileRules.extensions.map((extension) => toBareExtension(extension).toUpperCase()).join(', ')

/** `image/svg+xml` → `.svg` — the picker filters by extension, not by MIME. */
export const getFileRuleAcceptAttribute = (fileRules?: Nullable<FileRulesType>) =>
  (fileRules?.extensions || []).map((extension) => `.${toBareExtension(extension)}`).join(',')
